import { useRef, useEffect, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import {
    trackSearchCorrectionClick,
    trackAutoCorrectClick,
    trackAutoCorrectView,
    encodeForceSearchTrackingData,
    ATTRIBUTE_SEARCH_CORRECTION,
    ATTRIBUTE_SEARCH_AUTO_CORRECT,
} from './sbSharedForceSearchLinkTracking.ts';
import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants.ts';

// components
import { SbSharedSearchSuggestions } from '../SbSharedSearchSuggestions/SbSharedSearchSuggestions.jsx';

// styles
import styles from './SbSharedForceSearchLink.scss';

import { type SbSharedForceSearchLink_itemSearch$key } from './__generated__/SbSharedForceSearchLink_itemSearch.graphql.ts';

const itemSearchFragment = graphql`
    fragment SbSharedForceSearchLink_itemSearch on ItemSearchQueryConnection {
        pageType
        searchTerm
        searchCorrections {
            forceSearchUri
            originalSearchTerm
            semanticSearchTerm
            classificationSearchTerm
            spellCheckSource
        }
        searchResultData {
            vectorResultCount
        }
        ...SbSharedSearchSuggestions_itemSearch
    }
`;

type Props = {
    itemSearch: SbSharedForceSearchLink_itemSearch$key;
};

export const SbSharedForceSearchLink: FC<Props> = ({ itemSearch: itemSearchRef }) => {
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const { searchCorrections, pageType, searchTerm } = itemSearch;
    const {
        forceSearchUri,
        originalSearchTerm,
        semanticSearchTerm,
        classificationSearchTerm,
        spellCheckSource,
    } = searchCorrections || {};
    const trackingRef = useRef({
        originalTerm: originalSearchTerm,
        correctedTerm: searchTerm || semanticSearchTerm,
        semanticSearchTerm,
        spellCheckType: spellCheckSource,
    });

    useEffect(() => {
        const { correctedTerm, originalTerm, spellCheckType } = trackingRef.current;

        if (originalTerm && correctedTerm) {
            trackAutoCorrectView(correctedTerm, originalTerm, spellCheckType);
        }
    }, []);

    const overrideType = pageType === pageTypes.SEARCH ? 'classification' : 'semantic';

    if (originalSearchTerm && !(semanticSearchTerm || classificationSearchTerm)) {
        return (
            <span
                data-tn="force-search-link-spell-correct"
                className={styles.link}
                {...{
                    [ATTRIBUTE_SEARCH_AUTO_CORRECT]: encodeForceSearchTrackingData({
                        term: searchTerm,
                        originalSearchTerm,
                    }),
                }}
            >
                <FormattedMessage
                    id="sb.SbSharedForceSearchLink.spellCorrectText"
                    defaultMessage={'Search instead for "{searchTerm}"'}
                    values={{
                        searchTerm: (
                            <Link
                                href={forceSearchUri || ''}
                                onClick={() => {
                                    if (searchTerm) {
                                        trackAutoCorrectClick(searchTerm, originalSearchTerm);
                                    }
                                }}
                            >
                                {originalSearchTerm}
                            </Link>
                        ),
                    }}
                />
            </span>
        );
    } else if (semanticSearchTerm || classificationSearchTerm) {
        const term = originalSearchTerm || semanticSearchTerm || classificationSearchTerm;
        const hasVectorSearchResults = !!itemSearch?.searchResultData?.vectorResultCount;
        if (hasVectorSearchResults) {
            return null;
        }
        return (
            <span
                data-tn="force-search-link-semantic"
                className={styles.link}
                {...{
                    [ATTRIBUTE_SEARCH_CORRECTION]: encodeForceSearchTrackingData({
                        term,
                        originalSearchTerm,
                        overrideType,
                    }),
                }}
            >
                <FormattedMessage
                    id="sb.sharedForceSearchLink.semantic"
                    defaultMessage='Search for "{searchTerm}" in all categories'
                    values={{
                        searchTerm: (
                            <Link
                                href={forceSearchUri || ''}
                                onClick={() => {
                                    if (term) {
                                        trackSearchCorrectionClick(term, overrideType);
                                    }
                                }}
                            >
                                {term}
                            </Link>
                        ),
                    }}
                />
            </span>
        );
    } else {
        return (
            <SbSharedSearchSuggestions
                searchTerm={searchTerm}
                itemSearch={itemSearch}
                className={styles.link}
            />
        );
    }
};
